/**
 * One bill that has not become law.
 *
 * A sibling of the law page, for the other half of the corpus. A law is
 * finished and the question about it is what it does; a bill is unfinished
 * and the question is whether it will happen. Nobody can answer that for a
 * named bill, so the page answers the nearest thing that can be answered
 * accurately: here is how far it has got, and here is how often bills that
 * got that far became law.
 *
 * That rail is the top of the page on purpose. It is the whole difference
 * between this page and a link to congress.gov.
 *
 * The other load-bearing thing here is the line between two kinds of prose.
 * `summary` is Congress's own, written by the Congressional Research
 * Service, and it is a disclosure. `explainer` is written by a language
 * model, and it is not. They sit in separate sections, in different
 * containers, under different headings, and the generated one carries what
 * it was written from — because an explanation drawn from a nine-word title
 * and one drawn from the full filed text read exactly the same and are worth
 * very different amounts.
 */

import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, type BillDetail, type BillPerson, type BillVote, type Rung } from "../api";
import { useAsync } from "../useAsync";
import { LawNumber, Pill, Problem, Spinner } from "../components/bits";
import {
  asPercent,
  committeeName,
  congressUrl,
  explainerBody,
  grounding,
  oneIn,
  rail,
  reachedLabel,
  rollCallSentence,
  toneFor,
  type Step,
} from "../components/bill";
import { when } from "../format";

export function BillPage() {
  const { billId = "" } = useParams();
  const bill = useAsync(() => api.bill(billId), [billId]);

  // The bill payload is growing a `ladder` of its own. Until it has one, the
  // same four rates come off `/v1/moving-bills`, which has served them all
  // along — the same arithmetic over the same finished Congresses, so the
  // figure on this page and the figure on the list page cannot disagree.
  const context = useAsync(() => api.movingBills({ per_page: 1 }), []);

  if (bill.loading) {
    return (
      <div className="mx-auto max-w-4xl px-5">
        <Spinner label="Opening the bill…" />
      </div>
    );
  }

  if (bill.error || !bill.data) {
    const missing = bill.error instanceof ApiError && bill.error.status === 404;
    return (
      <div className="mx-auto max-w-2xl px-5 py-16">
        {missing ? (
          <div className="rounded-2xl border border-line bg-card p-10 text-center">
            <p className="font-display text-xl font-semibold">No bill with that id.</p>
            <p className="mt-2 text-muted">
              This holds the bills still alive in the current Congress. One that became law has a
              page of its own.
            </p>
          </div>
        ) : (
          <Problem
            error={bill.error ?? new Error("That bill is not in the corpus.")}
            retry={bill.reload}
          />
        )}
        <p className="mt-6 text-center">
          <Link to="/bills" className="font-medium text-brand hover:underline">
            ← What Congress is working on
          </Link>
        </p>
      </div>
    );
  }

  return <Bill data={bill.data} ladder={context.data?.ladder ?? null} />;
}

/** Split from the fetching so a test can render it against a payload. */
export function Bill({ data, ladder }: { data: BillDetail; ladder?: Rung[] | null }) {
  // Every collection is taken defensively. The endpoint is being added to
  // while this page is being read, and a field that has not arrived must
  // leave a gap on the page rather than take the page down.
  const votes = data.votes ?? [];
  const subjects = data.subjects ?? [];
  const committees = data.committees ?? [];
  const cosponsors = data.cosponsors ?? [];

  const steps = rail(data, ladder);
  const here = steps.find((step) => step.state === "here") ?? null;
  const placed = steps.some((step) => step.state !== "unknown" && step.key !== "introduced");
  const enacted = Boolean(data.became_law || data.law_number);
  const record = congressUrl(data);

  return (
    <article>
      <header className="border-b border-line bg-gradient-to-b from-brand-soft/50 to-paper">
        <div className="mx-auto max-w-4xl px-5 py-12">
          <p className="text-sm">
            <Link to="/bills" className="text-muted hover:text-ink">
              ← What Congress is working on
            </Link>
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="tnum text-xs font-semibold tracking-wide text-brand uppercase">
              {data.display_name || data.bill_id}
            </span>
            {enacted && <LawNumber value={data.law_number} />}
            {data.stage && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${toneFor(data.stage.key).chip}`}
              >
                {reachedLabel(data.stage.label, data.chambers)}
              </span>
            )}
            {data.policy_area && <Pill>{data.policy_area}</Pill>}
            {data.latest_action_date && (
              <span className="text-xs text-faint">{when(data.latest_action_date)}</span>
            )}
          </div>

          <h1 className="mt-4 font-display text-3xl leading-tight font-semibold text-balance sm:text-4xl">
            {data.title}
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-muted">
            {enacted ? (
              <>
                This bill became law.{" "}
                <Link to={`/law/${data.bill_id}`} className="font-medium text-brand hover:underline">
                  Read what it does
                </Link>
                .
              </>
            ) : (
              <>
                A bill before the {ordinal(data.congress)} Congress. It is not law, and nothing in
                it has any force unless it passes both chambers and is signed.
              </>
            )}
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-12">
        <Progress steps={steps} here={here} placed={placed} blurb={data.stage?.blurb ?? null} />

        <Explained data={data} />

        <Official data={data} />

        <Facts data={data} committees={committees} votes={votes} record={record} />

        <Voted votes={votes} />

        {cosponsors.length > 0 && (
          <Cosponsors people={cosponsors} counted={data.cosponsor_count} record={record} />
        )}

        {subjects.length > 0 && <Subjects subjects={subjects} />}

        <p className="mt-14 text-sm text-faint">
          Every fact above is read back from the record Congress publishes. The bill itself, its
          text and its full action history are on{" "}
          <a className="text-brand hover:underline" href={record} target="_blank" rel="noreferrer">
            congress.gov
          </a>
          .
        </p>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------- the rail --- */

/**
 * Where it has got to, and what that position has historically been worth.
 *
 * Six marks rather than the ladder's four. Introduction and enactment are
 * both disclosure — a date on the record and a public law number — so they
 * cost nothing to draw and they make the four inferred rungs between them
 * legible as the uncertain part of the journey.
 *
 * Every rate carries the count it was drawn from, in the cell, because a
 * bare percentage is not something a reader can weigh or disbelieve.
 */
function Progress({
  steps,
  here,
  placed,
  blurb,
}: {
  steps: Step[];
  here: Step | null;
  placed: boolean;
  blurb: string | null;
}) {
  const rated = steps.some((step) => step.odds !== null);
  // One colour for the whole of the filled track, taken from where the bill
  // stands. Colouring each segment with its own rung's tone leaves the first
  // two — which are the palest — looking unreached.
  const filled = toneFor(here?.key ?? "committee").bar;

  return (
    <section aria-labelledby="progress">
      <h2 id="progress" className="font-display text-2xl font-semibold">
        How far it has got, and what that has been worth
      </h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        Nobody can tell you whether this bill will pass. What can be counted is how often the bills
        that reached the same point in the finished Congresses went on to become law.
      </p>

      {/* The track carries no words, so it is hidden from a screen reader
          and the list of cells below is the real thing. */}
      <ol className="mt-6 flex gap-1" aria-hidden="true">
        {steps.map((step) => (
          <li
            key={step.key}
            className={`h-2 flex-1 rounded-full ${
              step.state === "done" || step.state === "here" ? filled : "bg-sunk"
            }`}
          />
        ))}
      </ol>

      <ol className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {steps.map((step) => {
          const tone = toneFor(step.key);
          const reached = step.state === "done" || step.state === "here";
          return (
            <li
              key={step.key}
              className={`rounded-2xl border p-3 ${
                step.state === "here"
                  ? `${tone.ring} bg-card shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]`
                  : "border-line bg-card"
              } ${step.state === "ahead" ? "opacity-55" : ""}`}
            >
              <p className={`text-sm leading-snug font-semibold ${reached ? "" : "text-muted"}`}>
                {step.label}
              </p>

              {step.state === "here" && (
                <p className={`mt-1 text-xs font-semibold ${tone.text}`}>It is here</p>
              )}

              {step.odds ? (
                <>
                  <p className={`tnum mt-2 font-display text-2xl font-semibold ${tone.text}`}>
                    {step.odds.share === null ? "—" : asPercent(step.odds.share)}
                  </p>
                  <p className="tnum mt-0.5 text-xs text-faint">
                    {step.odds.of === 0
                      ? "no bills got this far"
                      : `${step.odds.became_law.toLocaleString()} of ${step.odds.of.toLocaleString()} became law`}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-xs text-faint">
                  {step.key === "introduced"
                    ? "every bill starts here"
                    : step.key === "law"
                      ? "the end of the road"
                      : "no rate held for this rung"}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {!placed && (
        <p className="mt-4 rounded-xl border border-line bg-sunk/60 px-4 py-3 text-sm text-muted">
          How far this bill has got is not recorded here yet, so no rung above is marked. Anything
          shown against a rung is the historical rate for that rung, not a position for this bill.
        </p>
      )}

      {!rated && (
        <p className="mt-4 rounded-xl border border-line bg-sunk/60 px-4 py-3 text-sm text-muted">
          No pass rates are held for these rungs at the moment, so the rail shows the journey and
          not the odds. The rates are counted from the Congresses that have finished.
        </p>
      )}

      {here && here.odds && here.odds.share !== null && here.odds.of > 0 && (
        <div className="mt-5 rounded-2xl border border-line bg-card p-6">
          <p className="font-display text-xl font-semibold">
            {oneIn(here.odds.share)} of the bills that got this far became law.
          </p>
          <p className="tnum mt-2 leading-relaxed text-muted">
            {here.odds.became_law.toLocaleString()} of {here.odds.of.toLocaleString()} bills that
            reached “{here.label.toLowerCase()}” in the two most recent finished Congresses were
            enacted.
            {blurb ? ` ${blurb}` : ""}
          </p>
          <p className="mt-3 text-sm text-faint">
            That is a base rate for a position, not a forecast for this bill. It is counted only
            from Congresses that have finished, so nothing still in progress is mistaken for a
            failure.
          </p>
        </div>
      )}
    </section>
  );
}

/* --------------------------------------------------- the generated part --- */

/**
 * The machine-written account, labelled as one wherever it can be seen.
 *
 * The badge is above the prose rather than under it, the container does not
 * look like any other container on the site, and what it was written from is
 * stated in the same breath. A reader who takes in nothing but the heading
 * should still know that Congress did not write this.
 */
const STRENGTH: Record<string, { label: string; chip: string }> = {
  strong: { label: "Strong grounding", chip: "bg-brand/12 text-brand" },
  fair: { label: "Partial grounding", chip: "bg-sky-500/12 text-sky-700" },
  weak: { label: "Weak grounding", chip: "bg-amber-500/15 text-amber-800" },
  unknown: { label: "Grounding unrecorded", chip: "bg-sunk text-muted" },
};

function Explained({ data }: { data: BillDetail }) {
  const body = explainerBody(data.explainer);
  if (!body) return null;

  const source = grounding(data.explainer);
  const confidence = data.explainer?.confidence?.trim();
  const model = data.explainer?.model?.trim();
  const strength = STRENGTH[source.strength] ?? STRENGTH.unknown;

  return (
    <section className="mt-14" aria-labelledby="plain">
      <div className="flex flex-wrap items-center gap-2">
        {/* Solid, and the same whatever the grounding turns out to be. The
            strength chip beside it moves; this one may not, because it is
            the claim that always holds. */}
        <span className="rounded-full bg-ink px-2.5 py-1 text-xs font-semibold tracking-wide text-paper uppercase">
          Machine-generated
        </span>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${strength.chip}`}>
          {strength.label}
        </span>
      </div>

      <h2 id="plain" className="mt-3 font-display text-2xl font-semibold">
        In plain English
      </h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        Not written by Congress and not part of the record. A language model wrote this from{" "}
        {source.phrase}.
      </p>

      {/* Dashed and tinted, and nothing else on the site looks like this.
          The border is the label for anybody who arrived mid-page.

          The headline lives in here rather than at the top of the page. The
          law page can lead with a generated title because it was written
          from the statute; a bill's is regularly written from nine words of
          title, and the largest words on a bill's page are going to be the
          ones Congress gave it. */}
      <div className="mt-5 rounded-2xl border-2 border-dashed border-amber-500/50 bg-amber-50/50 p-6">
        {body.headline && (
          <p className="font-display text-xl leading-snug font-semibold text-balance">
            {body.headline}
          </p>
        )}

        {body.text && (
          <p className={`leading-relaxed whitespace-pre-line ${body.headline ? "mt-4" : ""}`}>
            {body.text}
          </p>
        )}

        {body.points.length > 0 && (
          <ul className={`space-y-4 ${body.headline || body.text ? "mt-5" : ""}`}>
            {body.points.map((point, n) => (
              <li key={n} className="flex gap-4">
                <span className="tnum mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-semibold text-amber-800">
                  {n + 1}
                </span>
                <p className="leading-relaxed">{point}</p>
              </li>
            ))}
          </ul>
        )}

        {body.affects && (
          <div className="mt-5 rounded-xl border border-amber-500/30 bg-card/60 p-4">
            <h3 className="font-display text-base font-semibold">Who it affects</h3>
            <p className="mt-1 leading-relaxed text-muted">{body.affects}</p>
          </div>
        )}

        <p className="mt-6 border-t border-amber-500/30 pt-4 text-sm leading-relaxed text-muted">
          <span className="font-semibold text-ink">Generated, not disclosed.</span> Every word in
          this box was written by {model || "a language model"} from {source.phrase}. Confidence:{" "}
          {confidence || "unstated"}. {source.caveat}
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------- the official part --- */

/** Congress's own summary, kept apart from the generated one and never
 *  quoted alongside it. The stage matters as much as the text: a summary of
 *  the introduced bill is not a summary of what passed the Senate. */
function Official({ data }: { data: BillDetail }) {
  const summary = data.summary?.trim();

  return (
    <section className="mt-14" aria-labelledby="official">
      <span className="rounded-full bg-sunk px-2.5 py-1 text-xs font-semibold tracking-wide text-muted uppercase">
        Official text
      </span>
      <h2 id="official" className="mt-3 font-display text-2xl font-semibold">
        Congress's own summary
      </h2>

      {summary ? (
        <>
          <p className="mt-1.5 max-w-3xl text-muted">
            Written by the Congressional Research Service and published by Congress
            {data.summary_stage ? `, describing the bill as ${lower(data.summary_stage)}` : ""}
            {data.summary_date ? ` on ${when(data.summary_date)}` : ""}. Quoted as published.
          </p>
          <blockquote className="mt-5 rounded-2xl border border-line bg-card p-6">
            <p className="leading-relaxed whitespace-pre-line">{summary}</p>
          </blockquote>
        </>
      ) : (
        <div className="mt-5 rounded-2xl border border-line bg-sunk/60 p-6">
          <p className="leading-relaxed text-muted">
            Congress has published no summary of this bill. That is ordinary for a bill early in
            its life — the Congressional Research Service writes one when there is something
            settled to describe.
          </p>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------- the fine print --- */

/** The block a reader arrives looking for, in the order congress.gov puts
 *  it: who filed it, where it went, what last happened to it, and whether
 *  anyone was made to go on record. */
function Facts({
  data,
  committees,
  votes,
  record,
}: {
  data: BillDetail;
  committees: string[];
  votes: BillVote[];
  record: string;
}) {
  return (
    <section className="mt-14" aria-labelledby="facts">
      <h2 id="facts" className="font-display text-2xl font-semibold">
        As filed
      </h2>

      <dl className="mt-5 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
        <Row label="Sponsor">
          {data.sponsor ? (
            <>
              <Person person={data.sponsor} />
              {data.introduced && (
                <span className="text-muted"> (Introduced {when(data.introduced)})</span>
              )}
            </>
          ) : (
            <span className="text-muted">
              No sponsor on the record{data.introduced ? ` (Introduced ${when(data.introduced)})` : ""}
            </span>
          )}
        </Row>

        <Row label="Committees">
          {committees.length > 0 ? (
            <ul className="space-y-0.5">
              {committees.map((code) => (
                <li key={code}>{committeeName(code)}</li>
              ))}
            </ul>
          ) : (
            <span className="text-muted">None on the record</span>
          )}
        </Row>

        <Row label="Latest Action">
          {data.latest_action ? (
            <>
              {data.latest_action_date && (
                <span className="tnum text-muted">{when(data.latest_action_date)} — </span>
              )}
              {data.latest_action}{" "}
              <a
                className="text-brand hover:underline"
                href={`${record}/all-actions`}
                target="_blank"
                rel="noreferrer"
              >
                (All {data.action_count ? `${data.action_count.toLocaleString()} ` : ""}actions)
              </a>
            </>
          ) : (
            <span className="text-muted">Nothing has happened to it since it was introduced.</span>
          )}
        </Row>

        <Row label="Roll Call Votes">
          {rollCallSentence(votes.length)}
          {votes.length === 0 && (
            <span className="text-muted">
              {" "}
              — which is how most bills move. A voice vote leaves no record of who was for it.
            </span>
          )}
        </Row>

        <Row label="Cosponsors">
          {typeof data.cosponsor_count === "number" ? (
            data.cosponsor_count === 1 ? (
              "1 cosponsor"
            ) : (
              `${data.cosponsor_count.toLocaleString()} cosponsors`
            )
          ) : (
            <span className="text-muted">Not recorded</span>
          )}
        </Row>

        {data.policy_area && <Row label="Policy area">{data.policy_area}</Row>}

        <Row label="On congress.gov">
          <a className="text-brand hover:underline" href={record} target="_blank" rel="noreferrer">
            {data.display_name || data.bill_id}, {ordinal(data.congress)} Congress
          </a>
        </Row>
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 px-5 py-3 sm:grid-cols-[11rem_1fr]">
      <dt className="text-sm font-semibold text-muted">{label}</dt>
      <dd className="text-sm leading-relaxed">{children}</dd>
    </div>
  );
}

/** A name as the chamber filed it, linked to their record here when the
 *  corpus holds one. The string already carries the party and the state. */
function Person({ person }: { person: BillPerson }) {
  const name = person.full_name?.trim() || person.bioguide || "Unnamed";
  if (!person.bioguide) return <span>{name}</span>;
  return (
    <Link to={`/member/${person.bioguide}`} className="font-medium text-brand hover:underline">
      {name}
    </Link>
  );
}

/* -------------------------------------------------------------- the roll --- */

/** Every recorded vote on the bill, which is not the same as every vote on
 *  it. Cloture, motions to table and motions to suspend the rules are all in
 *  here, and none of them is passage. */
function Voted({ votes }: { votes: BillVote[] }) {
  return (
    <section className="mt-14" aria-labelledby="voted">
      <h2 id="voted" className="font-display text-2xl font-semibold">
        Who voted
      </h2>

      {votes.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-line bg-sunk/60 p-6">
          <p className="font-medium">Nobody is on record.</p>
          <p className="mt-2 leading-relaxed text-muted">
            There have been no roll call votes on this bill. Most bills never get one: they die in
            committee, or they move on a voice vote or by unanimous consent, and neither leaves any
            record of who was for it. It is not the same as everyone agreeing — it means no one
            asked for a count.
          </p>
        </div>
      ) : (
        <>
          <p className="mt-1.5 text-muted">
            {rollCallSentence(votes.length)} — every recorded tally, including the procedural ones.
            A vote here is not necessarily a vote on passing the bill.
          </p>
          <ul className="mt-5 grid gap-5 sm:grid-cols-2">
            {votes.map((vote) => (
              <li key={vote.vote_id} className="rounded-2xl border border-line bg-card p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-lg font-semibold capitalize">
                    {vote.chamber}
                    {vote.roll !== null && <span className="tnum text-muted"> roll {vote.roll}</span>}
                  </h3>
                  <span className="shrink-0 text-xs text-faint">{when(vote.vote_date)}</span>
                </div>
                <p className="tnum mt-1 text-sm text-muted">
                  <strong className="font-semibold text-ink">{vote.yeas}</strong> for,{" "}
                  <strong className="font-semibold text-ink">{vote.nays}</strong> against
                  {vote.result ? ` — ${vote.result}` : ""}
                </p>
                <div className="mt-4">
                  <Tally vote={vote} />
                </div>
                {vote.question && (
                  <p className="mt-4 text-xs text-faint">On the question: {vote.question}</p>
                )}
                {vote.not_voting > 0 && (
                  <p className="mt-1 text-xs text-faint">
                    {vote.not_voting} did not vote, and are not counted either way.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** The split as one strip. The track is everyone who voted, so it is full
 *  whichever way they went — an empty track means nobody did, which is a
 *  different fact from a chamber voting no. */
function Tally({ vote }: { vote: BillVote }) {
  const cast = vote.yeas + vote.nays;
  return (
    <div
      className="flex h-2.5 overflow-hidden rounded-full bg-sunk"
      role="img"
      aria-label={`${vote.yeas} for, ${vote.nays} against`}
    >
      <div
        className="h-full bg-brand"
        style={{ width: `${cast ? (vote.yeas / cast) * 100 : 0}%` }}
      />
      <div
        className="h-full bg-brand opacity-25"
        style={{ width: `${cast ? (vote.nays / cast) * 100 : 0}%` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------ the extras --- */

function Cosponsors({
  people,
  counted,
  record,
}: {
  people: BillPerson[];
  counted: number | null;
  record: string;
}) {
  const shown = people.slice(0, 24);
  const missing = typeof counted === "number" ? counted - people.length : 0;

  return (
    <section className="mt-14" aria-labelledby="cosponsors">
      <h2 id="cosponsors" className="font-display text-2xl font-semibold">
        Who else put their name to it
      </h2>
      <ul className="mt-5 flex flex-wrap gap-2">
        {shown.map((person, n) => (
          <li
            key={person.bioguide ?? `${person.full_name ?? "unnamed"}-${n}`}
            className="rounded-full border border-line bg-card px-3 py-1.5 text-sm"
          >
            <Person person={person} />
            {person.original && <span className="text-faint"> · original</span>}
          </li>
        ))}
      </ul>
      {(people.length > shown.length || missing > 0) && (
        <p className="mt-3 text-sm text-faint">
          {people.length > shown.length && `${people.length - shown.length} more are held here. `}
          {missing > 0 && `${missing.toLocaleString()} more are on the record but not in this payload. `}
          <a className="text-brand hover:underline" href={record} target="_blank" rel="noreferrer">
            The full list is on congress.gov
          </a>
          .
        </p>
      )}
    </section>
  );
}

/** The subject terms Congress indexed it under. An appropriations bill can
 *  carry two hundred of them, so the list folds. */
function Subjects({ subjects }: { subjects: string[] }) {
  const first = subjects.slice(0, 12);
  const rest = subjects.slice(12);

  return (
    <section className="mt-14" aria-labelledby="subjects">
      <h2 id="subjects" className="font-display text-2xl font-semibold">
        What it is about
      </h2>
      <p className="mt-1.5 text-muted">
        The {subjects.length.toLocaleString()} subject{subjects.length === 1 ? "" : "s"} Congress
        indexed it under.
      </p>
      <ul className="mt-5 flex flex-wrap gap-2">
        {first.map((subject, n) => (
          <li key={`${subject}-${n}`}>
            <Pill>{subject}</Pill>
          </li>
        ))}
      </ul>
      {rest.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-muted hover:text-ink">
            The other {rest.length.toLocaleString()}
          </summary>
          <ul className="mt-3 flex flex-wrap gap-2">
            {rest.map((subject, n) => (
              <li key={`${subject}-${n}`}>
                <Pill>{subject}</Pill>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function lower(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}
